/**
 * <InspoFoldersGrid> - Iter 9.40 Pass C/F
 *
 * Surfaces ALL inspo folders visible to the user - admin (main), user (private),
 * host (private) - with per-category filtering. Click a folder → opens
 * <InspoFolderDetailModal> which lists images and allows add / delete.
 *
 * Backed by:
 *   GET /api/inspo/folders?scope=any[&category_id=…]
 *   POST /api/inspo/folders
 *   GET /api/inspo/folders/{id}/images
 *   POST /api/inspo/folders/{id}/images
 *   DELETE /api/inspo/folders/{id}/images/{image_id}
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Folder, Lock, Globe, Plus, Loader2, Image as ImageIcon, Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_META = {
  admin: { label: "Phygital",     icon: Globe, accent: "bg-purple-50 text-purple-700 border-purple-200" },
  user:  { label: "Private",      icon: Lock,  accent: "bg-gray-50 text-gray-700 border-gray-200" },
  host:  { label: "Host-curated", icon: Lock,  accent: "bg-rose-50 text-rose-700 border-rose-200" },
};

// Iter 9.40 Pass F - folder browse-into modal
const InspoFolderDetailModal = ({ folder, open, onClose }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const refresh = async () => {
    if (!folder) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspo/folders/${folder.folder_id}/images`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data.images || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    if (open && folder) refresh();
  }, [open, folder]); // eslint-disable-line

  const addImage = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspo/folders/${folder.folder_id}/images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ image_url: newUrl.trim() }),
      });
      if (res.ok) {
        toast.success("Image added");
        setNewUrl("");
        await refresh();
      } else {
        toast.error("Add failed");
      }
    } catch { toast.error("Failed"); }
    setAdding(false);
  };

  // Iter 9.40 Pass I - Cloudinary upload replaces URL paste.
  // Uploads to Cloudinary via /api/upload/cloudinary then attaches to the folder.
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAdding(true);
    const token = localStorage.getItem("session_token");
    try {
      for (const file of files) {
        // 1. Upload to Cloudinary
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", `inspo/${folder.folder_id}`);
        const upRes = await fetch(`${API}/upload/cloudinary`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!upRes.ok) { toast.error(`Upload failed for ${file.name}`); continue; }
        const upData = await upRes.json();
        const url = upData.secure_url || upData.url;
        if (!url) { toast.error("No URL returned"); continue; }
        // 2. Attach to folder
        await fetch(`${API}/inspo/folders/${folder.folder_id}/images`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ image_url: url, source: "cloudinary" }),
        });
      }
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
      await refresh();
    } catch (err) {
      toast.error("Upload error");
    }
    setAdding(false);
    event.target.value = ""; // reset input
  };

  const removeImage = async (image_id) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspo/folders/${folder.folder_id}/images/${image_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) await refresh();
    } catch { /* silent */ }
  };

  if (!folder) return null;
  const isOwner = folder.scope !== "admin"; // simplistic; backend enforces real perms

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid={`inspo-folder-detail-${folder.folder_id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="w-5 h-5" />
            {folder.name}
            <Badge variant="secondary" className="text-[10px] capitalize">{folder.scope}</Badge>
          </DialogTitle>
          {folder.description && <p className="text-xs text-gray-500">{folder.description}</p>}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500" data-testid="inspo-folder-detail-empty">
              <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              No images yet. {isOwner && "Paste an image URL below to start."}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {images.map((im) => (
                <div key={im.image_id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100" data-testid={`inspo-folder-image-${im.image_id}`}>
                  <img src={im.image_url} alt={im.caption || ""} className="w-full h-full object-cover" />
                  {isOwner && (
                    <button
                      onClick={() => removeImage(im.image_id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500/90 rounded-full text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
                      data-testid={`inspo-folder-image-remove-${im.image_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {isOwner && (
          <div className="border-t pt-3 space-y-2">
            {/* Iter 9.40 Pass I - Cloudinary file upload (replaces URL paste) */}
            <label className="flex-1 flex items-center gap-2 cursor-pointer border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:border-purple-400 hover:bg-purple-50 transition-colors" data-testid="inspo-folder-image-upload-label">
              <Upload className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 flex-1">
                {adding ? "Uploading..." : "Upload from your device (JPG, PNG, HEIC)"}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                disabled={adding}
                className="hidden"
                data-testid="inspo-folder-image-upload-input"
              />
            </label>
            <details className="text-xs text-gray-400">
              <summary className="cursor-pointer hover:text-gray-600">Or paste a URL</summary>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://… image URL"
                  data-testid="inspo-folder-image-add-url"
                />
                <Button onClick={addImage} disabled={adding || !newUrl.trim()} size="sm" data-testid="inspo-folder-image-add-submit">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </details>
            <div className="flex justify-end">
              <Button onClick={onClose} variant="outline" size="sm">
                <X className="w-4 h-4" /> Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const InspoFoldersGrid = ({ categoryId, onOpenFolder, defaultScope = "any" }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [openFolder, setOpenFolder] = useState(null);  // Iter 9.40 Pass F: detail modal

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ scope: defaultScope });
      if (categoryId) params.append("category_id", categoryId);
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspo/folders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    fetchFolders();
  }, [categoryId, defaultScope]); // eslint-disable-line

  const createFolder = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspo/folders`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim(), scope: "user", category_id: categoryId }),
      });
      if (res.ok) {
        toast.success("Folder created");
        setNewName("");
        setShowCreate(false);
        await fetchFolders();
      }
    } catch { toast.error("Failed"); }
    setCreating(false);
  };

  const handleFolderClick = (f) => {
    setOpenFolder(f);
    onOpenFolder?.(f);
  };

  const adminFolders = folders.filter((f) => f.scope === "admin");
  const myFolders   = folders.filter((f) => f.scope === "user");
  const hostFolders = folders.filter((f) => f.scope === "host");

  const Section = ({ title, items, scope }) => {
    if (items.length === 0) return null;
    const meta = SCOPE_META[scope];
    const Icon = meta.icon;
    return (
      <div>
        <h4 className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {title}
          <Badge variant="secondary" className="text-[10px] py-0">{items.length}</Badge>
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((f) => (
            <button
              key={f.folder_id}
              type="button"
              onClick={() => handleFolderClick(f)}
              data-testid={`inspo-folder-${f.folder_id}`}
              className={`text-left p-3 rounded-xl border transition-all hover:shadow-md ${meta.accent}`}
            >
              <Folder className="w-5 h-5 mb-1.5" />
              <div className="font-medium text-sm truncate">{f.name}</div>
              <div className="text-[10px] opacity-60 mt-0.5">
                {f.image_count || 0} images
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="inspo-folders-grid">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Inspo folders</h3>
        {/* Iter 9.47.u - Top-level "New folder" removed. Main category folders are admin-controlled
            (sync from Airtable). Users create subfolders inside each category folder. */}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : folders.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500" data-testid="inspo-folder-empty">
          {/* Iter 9.47.u - empty-state copy simplified per user feedback. Main
              category folders come from Airtable; subfolders live inside each one. */}
          Your category folders will appear here once your hosts publish their inspo.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Iter 9.40 Slice I.f - "Phygital curated" folder removed per user direction.
              Curated inspo lives inside admin tooling only; clients only see their own + their hosts'. */}
          <Section title="Your private folders" items={myFolders} scope="user" />
          <Section title="From your hosts" items={hostFolders} scope="host" />
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-sm" data-testid="inspo-folder-create-modal">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Wedding day looks"
            data-testid="inspo-folder-create-name"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" onClick={createFolder} disabled={creating || !newName.trim()} data-testid="inspo-folder-create-submit">
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <InspoFolderDetailModal
        folder={openFolder}
        open={!!openFolder}
        onClose={() => setOpenFolder(null)}
      />
    </div>
  );
};

export default InspoFoldersGrid;
